import { NavLink } from "react-router-dom";
import clsx from "clsx";

import { CaptureButton } from "./CaptureButton";

type NavItem = {
  to: string;
  label: string;
  glyph: "book" | "shelf";
};

const ITEMS: NavItem[] = [
  { to: "/scrapbook", label: "Scrapbook", glyph: "book" },
  { to: "/collections", label: "Collections", glyph: "shelf" },
];

/**
 * Two destinations and one action. On phones this is a bottom bar so the capture
 * button lands under the thumb; on wider screens the same links sit in the
 * masthead.
 */
export function Nav({ variant }: { variant: "bar" | "masthead" }) {
  if (variant === "masthead") {
    return (
      <nav aria-label="Main" className="hidden items-center gap-6 sm:flex">
        {ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              clsx(
                "touch inline-flex items-center border-b-2 pt-1 pb-0.5 font-label text-sm font-medium tracking-wide transition-colors",
                isActive
                  ? "border-ochre text-ink"
                  : "border-transparent text-ink-soft hover:border-rule hover:text-ink",
              )
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
    );
  }

  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-rule bg-paper/95 backdrop-blur-[2px] sm:hidden"
    >
      <div className="grain relative flex items-end justify-around gap-2 px-4 pt-2 pb-safe">
        {ITEMS.slice(0, 1).map((item) => (
          <BarLink key={item.to} item={item} />
        ))}

        {/* Thumb-reachable, and deliberately the largest thing on screen. */}
        <div className="-mt-6 flex shrink-0 flex-col items-center">
          <CaptureButton variant="thumb" />
        </div>

        {ITEMS.slice(1).map((item) => (
          <BarLink key={item.to} item={item} />
        ))}
      </div>
    </nav>
  );
}

function BarLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        clsx(
          "touch flex flex-1 flex-col items-center gap-1 py-1.5 transition-colors",
          isActive ? "text-ochre" : "text-ink-soft hover:text-ink",
        )
      }
    >
      {({ isActive }) => (
        <>
          <NavGlyph glyph={item.glyph} active={isActive} />
          <span className="field-label-tight">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

function NavGlyph({ glyph, active }: { glyph: NavItem["glyph"]; active: boolean }) {
  const stroke = "currentColor";
  const fill = active ? "currentColor" : "none";

  if (glyph === "book") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="size-6" fill="none" strokeWidth="1.5">
        <rect x="3.5" y="3.5" width="17" height="17" stroke={stroke} />
        <path d="M8 8h8M8 12h8M8 16h5" stroke={stroke} strokeDasharray="2 2" />
        <rect x="13" y="14" width="6" height="4.5" stroke={stroke} fill={fill} opacity={active ? 0.25 : 1} />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-6" fill="none" strokeWidth="1.5">
      <rect x="3" y="5" width="4.5" height="14" stroke={stroke} fill={fill} opacity={active ? 0.25 : 1} />
      <rect x="9.75" y="5" width="4.5" height="14" stroke={stroke} />
      <rect x="16.5" y="5" width="4.5" height="14" stroke={stroke} />
      <path d="M3 19h18" stroke={stroke} />
    </svg>
  );
}
